import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Users, BookOpen, GraduationCap, Church, Globe, Star, Heart } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../Libs/firebase-config.mjs';

const JourneySection = ({ darkMode }) => {
  const [journeyData, setJourneyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadJourneyData();
  }, []);

  const loadJourneyData = async () => {
    try {
      const docRef = doc(db, 'journeySection', 'content');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setJourneyData(docSnap.data());
      } else {
        // Fallback to default data if no data in Firestore
        setJourneyData({
          title: "Peter Kyengo's Journey",
          subtitle: "My Ministry Journey",
          description: "From a Muslim background to establishing FLEC across five counties - a testament to God's grace and transformative power.",
          milestones: [
            { 
              year: "1995", 
              title: "Salvation Experience", 
              desc: "Accepted the Lord as my personal savior, beginning a transformative journey from a Muslim background to becoming a first-generation Christian.",
              location: "Kenya",
              icon: "Heart",
              img: "https://res.cloudinary.com/dbacioerk/image/upload/v1763653758/photo_2025-03-16_14-41-58_tto0dv.webp"
            },
            { 
              year: "1998", 
              title: "Holy Spirit Baptism", 
              desc: "Filled by the Holy Spirit with evident work of regeneration in my walk with God, deepening my yearning for spiritual growth and service.",
              location: "Kenya",
              icon: "Star",
              img: "https://res.cloudinary.com/dbacioerk/image/upload/v1763653720/photo_2025-04-02_16-35-43_d1fjzu.webp"
            },
            { 
              year: "2001", 
              title: "Call to Ministry", 
              desc: "Discovered a deep-seated passion for the lost and joined the International School of Missions under Archbishop Henry Z. Mulandi's tutelage.",
              location: "Christian Churches International",
              icon: "GraduationCap",
              img: "https://res.cloudinary.com/dbacioerk/image/upload/v1763653742/photo_2025-04-02_16-35-19_h7ut9i.webp"
            },
            { 
              year: "2005", 
              title: "Church Planting Missions", 
              desc: "Launched church planting engagements through ISOM and ACMI across central, northern, and lower eastern Kenya, trusting God for daily providence.",
              location: "Multiple Kenyan Regions",
              icon: "Church",
              img: "https://res.cloudinary.com/dbacioerk/image/upload/v1763653773/DSC_0514_kmus8u.webp"
            },
            { 
              year: "2007", 
              title: "Youth Pastor", 
              desc: "Recalled from mission field by spiritual father Bishop Paul Mutua and authorized as Youth Pastor, serving in various behind-the-scenes roles.",
              location: "Kenya",
              icon: "Users",
              img: "https://res.cloudinary.com/dbacioerk/image/upload/v1763653709/photo_2025-05-27_22-36-24_k0kapz.webp"
            },
            { 
              year: "2018", 
              title: "FLEC Foundation", 
              desc: "Founded Faith Life Evangelistic Church in Machakos with a mandate to exalt Christ, equip the saints, and evangelize the world.",
              location: "Machakos, Kenya",
              icon: "Globe",
              img: "https://res.cloudinary.com/dbacioerk/image/upload/v1763653698/photo_2025-07-02_18-18-55_by6sb2.webp"
            },
            { 
              year: "Present", 
              title: "Growing Ministry", 
              desc: "FLEC now has a growing presence in five counties with over 20 years of ministry experience, continuing to bring deliverance and transformation.",
              location: "Five Kenyan Counties",
              icon: "MapPin",
              img: "https://res.cloudinary.com/dbacioerk/image/upload/v1763653681/shalem__64_t9urqp.webp"
            }
          ]
        });
      }
    } catch (err) {
      console.error('Error loading journey section:', err);
      setError('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <section className={`py-32 px-8 ${darkMode ? 'bg-slate-900' : 'bg-gradient-to-br from-slate-50 to-amber-50/30'} transition-colors duration-300 overflow-hidden`}>
        <div className="max-w-7xl mx-auto">
          {/* Header Skeleton */}
          <div className="text-center mb-20 space-y-6">
            <div className="inline-block">
              <div className={`w-20 h-20 rounded-full ${darkMode ? 'bg-slate-800' : 'bg-white'} shadow-lg animate-pulse`} />
            </div>
            <div className={`h-4 bg-slate-400 rounded w-48 mx-auto ${darkMode ? 'bg-slate-600' : ''}`}></div>
            <div className={`h-12 bg-slate-400 rounded w-64 mx-auto ${darkMode ? 'bg-slate-600' : ''}`}></div>
            <div className={`h-6 bg-slate-400 rounded w-96 max-w-full mx-auto ${darkMode ? 'bg-slate-600' : ''}`}></div>
          </div>

          {/* Timeline Skeleton */}
          <div className="space-y-24 lg:space-y-32">
            {[1, 2, 3, 4, 5, 6, 7].map((_, index) => (
              <div key={index} className={`flex flex-col lg:flex-row items-center gap-12 ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}>
                <div className="flex-1 w-full lg:max-w-2xl">
                  <div className={`${darkMode ? 'bg-slate-800' : 'bg-white'} p-10 rounded-2xl shadow-lg border ${darkMode ? 'border-slate-700' : 'border-slate-200'} h-full min-h-[400px] animate-pulse`}>
                    <div className="space-y-4">
                      <div className={`h-8 bg-slate-400 rounded w-32 ${darkMode ? 'bg-slate-600' : ''}`}></div>
                      <div className={`h-12 bg-slate-400 rounded w-full ${darkMode ? 'bg-slate-600' : ''}`}></div>
                      <div className={`h-24 bg-slate-400 rounded w-full ${darkMode ? 'bg-slate-600' : ''}`}></div>
                      <div className={`h-6 bg-slate-400 rounded w-48 ${darkMode ? 'bg-slate-600' : ''}`}></div>
                    </div>
                  </div>
                </div>
                <div className="flex-1 w-full lg:max-w-xl">
                  <div className={`h-[500px] bg-slate-400 rounded-2xl animate-pulse ${darkMode ? 'bg-slate-600' : ''}`}></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Show error state
  if (error) {
    return (
      <section className={`py-32 px-8 ${darkMode ? 'bg-slate-900' : 'bg-gradient-to-br from-slate-50 to-amber-50/30'} transition-colors duration-300 overflow-hidden`}>
        <div className="max-w-7xl mx-auto text-center">
          <div className={`${darkMode ? 'bg-slate-800' : 'bg-white'} p-12 rounded-2xl shadow-lg border ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
            <div className={`text-lg font-bold ${darkMode ? 'text-red-400' : 'text-red-600'} mb-4`}>
              Error Loading Content
            </div>
            <div className={`text-md ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
              {error}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Use the actual data from Firestore
  const data = journeyData;

  // Icon mapping
  const iconMap = {
    Heart: Heart,
    Star: Star,
    GraduationCap: GraduationCap,
    Church: Church,
    Users: Users,
    Globe: Globe,
    MapPin: MapPin,
    BookOpen: BookOpen
  };

  return (
    <section className={`py-32 px-8 ${darkMode ? 'bg-slate-900' : 'bg-gradient-to-br from-slate-50 to-amber-50/30'} transition-colors duration-300 overflow-hidden`}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
            className="inline-block mb-6"
          >
            <div className={`w-20 h-20 rounded-full ${darkMode ? 'bg-slate-800' : 'bg-white'} shadow-lg flex items-center justify-center border ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
              <MapPin className={`w-10 h-10 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`} />
            </div>
          </motion.div>
          <div className={`text-sm uppercase tracking-widest ${darkMode ? 'text-slate-400' : 'text-slate-500'} font-medium mb-4`}>
            {data.subtitle || 'My Ministry Journey'}
          </div>
          <h2 className={`text-5xl md:text-6xl font-bold ${darkMode ? 'text-white' : 'text-slate-900'} mb-6`}>
            {data.title || "Peter Kyengo's Journey"}
          </h2>
          <p className={`text-xl ${darkMode ? 'text-slate-300' : 'text-slate-600'} max-w-3xl mx-auto leading-relaxed`}>
            {data.description || "From a Muslim background to establishing FLEC across five counties - a testament to God's grace and transformative power."}
          </p>
        </motion.div>

        {/* Journey Timeline */}
        <div className="relative">
          {/* S-Curve Line */}
          <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full hidden lg:block">
            <motion.div
              initial={{ scaleY: 0 }}
              whileInView={{ scaleY: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.5, ease: "easeOut" }}
              className={`w-full h-full ${darkMode ? 'bg-gradient-to-b from-slate-600 via-slate-500 to-slate-400' : 'bg-gradient-to-b from-slate-300 via-slate-200 to-slate-300'} rounded-full`}
            />
          </div>

          {/* Connection Dots */}
          <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-full hidden lg:block">
            {data.milestones.map((_, index) => (
              <motion.div
                key={index}
                initial={{ scale: 0, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2, type: "spring", stiffness: 200 }}
                className={`absolute left-1/2 transform -translate-x-1/2 w-4 h-4 rounded-full ${darkMode ? 'bg-slate-400' : 'bg-slate-300'} shadow-sm border ${darkMode ? 'border-slate-500' : 'border-slate-200'}`}
                style={{ top: `${(index / (data.milestones.length - 1)) * 100}%` }}
              />
            ))}
          </div>

          {/* Milestones */}
          <div className="space-y-24 lg:space-y-32">
            {data.milestones.map((milestone, index) => (
              <JourneyMilestone 
                key={index} 
                milestone={milestone} 
                index={index} 
                darkMode={darkMode}
                iconMap={iconMap}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const JourneyMilestone = ({ milestone, index, darkMode, iconMap }) => {
  const Icon = iconMap[milestone.icon] || Heart;
  const isEven = index % 2 === 0;

  // Standardized animation variants
  const cardVariants = {
    hidden: { 
      opacity: 0, 
      x: isEven ? -50 : 50,
      y: 20
    },
    visible: { 
      opacity: 1, 
      x: 0,
      y: 0,
      transition: {
        duration: 0.8,
        delay: index * 0.15,
        ease: "easeOut"
      }
    }
  };

  const imageVariants = {
    hidden: { 
      opacity: 0, 
      scale: 0.8,
      rotate: isEven ? -5 : 5
    },
    visible: { 
      opacity: 1, 
      scale: 1,
      rotate: 0,
      transition: {
        duration: 0.8,
        delay: index * 0.15 + 0.2,
        ease: "easeOut"
      }
    }
  };

  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      className={`relative flex flex-col lg:flex-row items-center gap-12 ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}
    >
      {/* Content Card */}
      <motion.div 
        variants={cardVariants}
        className="flex-1 w-full lg:max-w-2xl"
      >
        <motion.div
          whileHover={{ y: -5 }}
          className={`${darkMode ? 'bg-slate-800' : 'bg-white'} p-10 rounded-2xl shadow-lg border ${darkMode ? 'border-slate-700' : 'border-slate-200'} relative overflow-hidden group transition-all duration-300 h-full min-h-[400px] flex flex-col justify-center`}
        >
          {/* Subtle background pattern */}
          <div className="absolute inset-0 opacity-[0.02] group-hover:opacity-[0.03] transition-opacity duration-500 bg-gradient-to-br from-slate-400 to-slate-600" />
          
          {/* Year Badge */}
          <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.15 + 0.1, type: "spring", stiffness: 200 }}
            className={`inline-flex items-center gap-3 px-4 py-2 rounded-full ${darkMode ? 'bg-slate-700' : 'bg-slate-100'} mb-6 border ${darkMode ? 'border-slate-600' : 'border-slate-200'}`}
          >
            <div className={`w-2 h-2 rounded-full ${darkMode ? 'bg-slate-300' : 'bg-slate-600'}`} />
            <span className={`font-semibold ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
              {milestone.year}
            </span>
          </motion.div>

          {/* Icon and Title */}
          <div className="flex items-center gap-4 mb-6">
            <div className={`p-3 rounded-xl ${darkMode ? 'bg-slate-700' : 'bg-slate-100'} border ${darkMode ? 'border-slate-600' : 'border-slate-200'}`}>
              <Icon className={`w-7 h-7 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`} />
            </div>
            <h3 className={`text-3xl md:text-4xl font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
              {milestone.title}
            </h3>
          </div>

          {/* Description */}
          <p className={`text-xl ${darkMode ? 'text-slate-300' : 'text-slate-600'} mb-8 leading-relaxed flex-grow`}>
            {milestone.desc}
          </p>

          {/* Location */}
          <div className="flex items-center gap-3">
            <MapPin className={`w-5 h-5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`} />
            <span className={`text-base ${darkMode ? 'text-slate-400' : 'text-slate-500'} font-medium`}>
              {milestone.location}
            </span>
          </div>
        </motion.div>
      </motion.div>

      {/* Image Card */}
      <motion.div 
        variants={imageVariants}
        className="flex-1 w-full lg:max-w-xl"
      >
        <motion.div
          whileHover={{ scale: 1.02 }}
          className="relative group h-full min-h-[500px]"
        >
          {/* Main Image with refined styling */}
          <div className="relative z-10 overflow-hidden rounded-2xl h-full">
            <img 
              src={milestone.img} 
              alt={milestone.title}
              className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
              onError={(e) => {
                // Fallback if image fails to load
                e.target.src = 'https://res.cloudinary.com/dbacioerk/image/upload/v1763653758/photo_2025-03-16_14-41-58_tto0dv.webp';
              }}
            />
            {/* Subtle overlay */}
            <div className={`absolute inset-0 rounded-2xl ${darkMode ? 'bg-slate-900/20' : 'bg-white/10'} transition-opacity duration-300 group-hover:opacity-0`} />
          </div>

          {/* Subtle border effect */}
          <div className={`absolute -inset-2 rounded-2xl ${darkMode ? 'bg-slate-600/20' : 'bg-slate-200/50'} blur-sm transition-all duration-300 group-hover:blur-md group-hover:opacity-70`} />
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default JourneySection;